<template>
  <div id="payment">
    <div class="container">

      <TipBox info v-if="mustHaveLiveMode">
        <div v-html="$t('payment_settings.live_mode_mandatory')"></div>
      </TipBox>

      <div class="page-title">
        <div class="subtitle">{{ $t("companies.settings.payment") }}</div>
        <button class="btn btn-primary" @click="save" v-if="canEditPaymentSettings">{{ $t("save") }}</button>
      </div>

      <Tabs>
        <TabSection :title="$t('payment_settings.online_payment')" collapsible :collapsed="recipientAccounts.length > 0">
          <div id="recipient-accounts-container">
            <RecipientAccount v-for="(recipientAccount, index) of recipientAccounts" :key="`ra_${index}`"
              ref="payment"
              :recipient-account="recipientAccount"
              :canActivate="true"
              :canRemove="!recipientAccount.id"
              v-on:remove="removeRecipientAccount(index)"
            />
          </div>
          <button class="btn btn-primary" @click="addRecipientAccount">{{ $t("payment_settings.add_new") }}</button>
        </TabSection>

        <TabSection :title="$t('payment_settings.offline_payment')" collapsible>
          <div class="columns">
            <div class="column">
              <div class="subtitle">{{ $t('payment_settings.add_offline_method') }}</div>
              <div class="description">
                {{ $t('payment_settings.add_offline_method_description') }}
              </div>
              <div class="group">
                <label>{{ $t('payment_settings.offline_payment_method') }}</label>
                <input type="text" v-model="offlinePayment.custom" @keydown.space.prevent>
              </div>
              <button class="btn btn-primary" @click="addCustomOfflineMethod" :disabled="!this.offlinePayment.custom">{{
                $t("payment_settings.add_new_offline") }}</button>
            </div>
            <div class="column">
              <div class="subtitle">{{ $t('payment_settings.offline_methods') }}</div>
              <div class="group" v-for="name of offlinePayment.all" :key="`offline_${name}`">
                <input :id="`chk_${name}`" type="checkbox" :value="name" v-model="offlinePayment.selected">
                <label :for="`chk_${name}`">{{ $t(name) }}</label>
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection title="Taxes" collapsible>
          <div class="columns">
            <div class="column">
              <div class="subtitle">{{ $t('payment_settings.tax_group') }}</div>
              <div class="group input-group">
                <select v-model="selectedTaxGroup">
                  <option v-for="tg of taxGroups" :value="tg.id" :key="tg.id">{{ tg.uniqueName }}</option>
                </select>
                <div class="remove btn btn-primary" @click="selectedTaxGroup = null">&times;</div>
              </div>
              <div class="subtitle">{{ $t('payment_settings.tax_number') }}</div>
              <div class="group" v-for="tax of taxesOfSelectedGroup" :key="tax.uniqueName">
                <label>{{ tax.uniqueName }}</label>
                <input type="text" v-model="taxNumbers[tax.uniqueName]">
              </div>
              <div class="group" v-if="taxesOfSelectedGroup.length === 0 || taxNumbers['VAT']">
                <label>VAT</label>
                <input type="text" v-model="taxNumbers['VAT']">
              </div>
            </div>
          </div>
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="canEditPaymentSettings">
        <button class="btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import RecipientAccount from "@/views/companies/settings/RecipientAccount";
  import TipBox from "@/components/tools/TipBox";

  export default {

    name: "payment",

    components: { RecipientAccount, TipBox },

    data() {
      return {
        branchId: null,
        companyId: null,
        recipientAccounts: [],

        selectedTaxGroup: null,
        taxGroups: [],
        taxNumbers: {},

        offlinePayment: {
          selected: [],
          all: ["cash", "interac", "mastercard", "visa", "amex"],
          custom: "",
          customPaymentMethods: []
        },
      };
    },

    computed: {
      taxesOfSelectedGroup() {
        for (let taxGroup of this.taxGroups) {
          if (taxGroup.id === this.selectedTaxGroup) {
            return taxGroup.taxes
          }
        }
        return [];
      },
      canViewPaymentSettings() {
        return this.$store.getters.loggedUserHasViewPaymentSettingsPermission;
      },
      canEditPaymentSettings() {
        return this.$store.getters.loggedUserHasEditPaymentSettingsPermission;
      },
      branch() {
        return this.$store.state.contextBranch;
      },
      mustHaveLiveMode() {
        let config = Parameters.paymentTestModeAllowedBranches;
        if (Array.isArray(config) && config.length > 0 && this.branch) {
          return config.indexOf(this.branch.nameCanonical) === -1;
        }
        return false;
      },
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "companies.settings.payment";
      this.$store.state.pageIcon = "";
      this.branchId = this.$route.query.branch;
      this.companyId = this.$route.query.company;

      if (this.companyId) {
        await this.load();
      } else {
        await this.$router.push("/");
      }

      if (!this.canViewPaymentSettings) {
        await this.$router.push("/");
      }

      this.$nextTick(() => {
        if (!this.canEditPaymentSettings) {
          window.lockFields();
          window.hideButtons(this.$el);
        }
      })
    },

    methods: {

      ...mapActions(["getCompany", "saveCompany", "getTaxGroups"]),

      addRecipientAccount() {
        this.recipientAccounts.push({});
      },

      removeRecipientAccount(e) {
        this.recipientAccounts.splice(e, 1);
      },

      async load() {
        this.$store.commit("showSpinner", true);

        Promise
          .all([
            this.getCompany({ branchId: this.branchId, companyId: this.companyId, augment: 'recipient' }),
            this.getTaxGroups()
          ])
          .then(([company, taxGroups]) => {
            this.offlinePayment.selected = company.offlinePaymentMethods;

            if (company.onlineOrdering && company.onlineOrdering.hotel && company.onlineOrdering.hotel.activated) {
              this.offlinePayment.all.push("room-charge");
            }

            if (company.customPaymentMethods) {
              this.offlinePayment.customPaymentMethods = company.customPaymentMethods;
            }

            let allOfflinePaymentMethods = this.offlinePayment.all.concat(company.offlinePaymentMethods);
            this.offlinePayment.all = [...new Set(allOfflinePaymentMethods)];

            this.recipientAccounts = company.recipientAccounts || [];
            for (let recAccount of this.recipientAccounts) {
              recAccount.activated = company.paymentProcessors.includes(recAccount.type)
            }

            this.selectedTaxGroup = company.taxGroup;
            this.taxNumbers = Array.isArray(company.taxNumbers) ? {} : (company.taxNumbers || {});
            this.taxGroups = taxGroups.data.map(x => ({
              id: x.id,
              uniqueName: x.uniqueName,
              taxes: x.taxes.map(y => ({
                uniqueName: y.uniqueName,
                name: y.name,
              }))
            }));
          })
          .catch(() => {
            toast(this.$t("error.no_server"), "error");
          })
          .finally(() => {
            this.$store.commit("showSpinner", false);
          });
      },

      async save() {
        if (!this.canEditPaymentSettings) {
          return;
        }
        let data = {};
        let recipientAccounts = [];
        let paymentProcessors = [];
        let paymentRefs = [];

        paymentRefs = paymentRefs.concat(this.$refs.payment ? this.$refs.payment : []);
        for (let $ref of paymentRefs) {
          let isActivated = $ref.isActivated();
          let recipientAccount = $ref.getData();
          if (recipientAccount) {
            recipientAccounts.push(recipientAccount);
          }
          if (recipientAccount && isActivated) {
            paymentProcessors.push(recipientAccount.type);
          }
        }
        if (this.offlinePayment.activated || paymentProcessors.length === 0) {
          paymentProcessors.push('none');
        }

        if (this.offlinePayment.customPaymentMethods && this.offlinePayment.customPaymentMethods.length > 0) {
          data.customPaymentMethods = this.offlinePayment.customPaymentMethods.filter(p => this.offlinePayment.selected.includes(p));
        }

        data.recipientAccounts = recipientAccounts;
        data.paymentProcessors = paymentProcessors;
        data.offlinePaymentMethods = this.offlinePayment.selected;
        data.taxGroup = this.selectedTaxGroup;
        data.taxNumbers = this.taxNumbers;


        let response = await this.saveCompany({
          branchId: this.branchId,
          companyId: this.companyId,
          augment: 'recipient',
          data: data
        });
        if (response) {
          toast(this.$t("company.success"), "success");
        } else {
          toast(this.$t('Unexpected error on save'), "error");
        }
      },
      addCustomOfflineMethod() {
        this.offlinePayment.selected.push(this.offlinePayment.custom);
        this.offlinePayment.all.push(this.offlinePayment.custom);
        this.offlinePayment.customPaymentMethods.push(this.offlinePayment.custom);
        this.offlinePayment.custom = "";
      }
    }
  }
</script>

<style lang="scss" scoped>
  #payment {
    display: flex;
  }

  #recipient-accounts-container>div {
    border-bottom: dashed 1px #d6d6d6;
    margin-bottom: 40px;
    padding-bottom: 20px;
  }

  .input-group {
    display: flex;
    flex-direction: row;

    select {
      flex-grow: 1;
    }

    .btn.remove {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 24px;
      line-height: 10px;
      padding: 5px 14px;
      margin-left: 10px;
    }
  }

  .description {
    padding-bottom: 20px;
  }
</style>
